import { createElement } from "react";
import type { RowData } from "@tanstack/react-table";
import { AccountCell } from "../cells/AccountCell";
import type {
    DataGridAccountValue,
    DataGridCounterpartyValue,
    DataGridEntityValue,
} from "../cells/cellValueTypes";
import { CounterpartyCell } from "../cells/CounterpartyCell";
import { EntityCell } from "../cells/EntityCell";
import { FileNameCell } from "../cells/FileNameCell";
import { LinkCell } from "../cells/LinkCell";
import { DateCell, MoneyCell, NumberCell } from "../cells/FormattedCell";
import { StatusCell } from "../cells/StatusCell";
import { TextCell } from "../cells/TextCell";
import { createDisplayColumns } from "./createDisplayColumns";
import { createListColumns } from "./createListColumns";
import { createListEditors } from "./listEditors";
import { createToggleSlot } from "./toggleSlot";
import {
    cellOf,
    dataColumn,
    type Nullable,
    toAccessor,
} from "./columnFactories";
import { toAvatarDisplay, toStatusSkeleton } from "./columnSkeletons";
import {
    createStatusSort,
    sortDateOnly,
    sortNumber,
    sortText,
} from "./sortFns";
import type {
    DataGridAccountColumnOptions,
    DataGridEntityColumnOptions,
    DataGridFileNameColumnOptions,
    DataGridLinkColumnOptions,
    DataGridTextColumnOptions,
} from "./kindOptions";
import type {
    DataGridColumnOptions,
    DataGridCustomColumnOptions,
    DataGridDateColumnOptions,
    DataGridMoneyColumnOptions,
    DataGridNumberColumnOptions,
    DataGridStatusColumnOptions,
    DataGridValueSource,
} from "./types";

type TextValue = Nullable<string>;
type NumberValue = Nullable<number>;

/**
 * Kind builders for `TData`. Each returns a plain TanStack column definition
 * with typed meta, the kind's sort function, alignment, size, and default
 * cell. Call once at module level and keep the resulting columns array stable.
 *
 * @example
 * const col = createColumns<Statement>();
 * const columns = [
 *     col.select(),
 *     col.fileName("fileName", { header: "File name", fill: 2, showIcon: true }),
 *     col.status("status", { header: "Status", order, statuses }),
 *     col.money("amount", { header: "Amount" }),
 *     col.actions({ render: StatementActions }),
 * ];
 */
export const createColumns = <TData extends RowData>() => ({
    edit: createListEditors<TData>(),
    toggle: createToggleSlot<TData>(),
    ...createListColumns<TData>(),
    ...createDisplayColumns<TData>(),
    text: (
        source: DataGridValueSource<TData, TextValue>,
        options: DataGridTextColumnOptions<TData>,
    ) =>
        dataColumn<TData>({
            kind: "text",
            ...toAccessor(source),
            options,
            sortFn: sortText,
            cell: cellOf<TData>(TextCell),
        }),
    fileName: (
        source: DataGridValueSource<TData, TextValue>,
        options: DataGridFileNameColumnOptions<TData>,
    ) =>
        dataColumn<TData>({
            kind: "fileName",
            ...toAccessor(source),
            options,
            sortFn: sortText,
            skeleton: options.showIcon ? "fileText" : "text",
            cell: (context, meta) =>
                createElement(FileNameCell<TData>, {
                    context,
                    meta,
                    showIcon: options.showIcon ?? false,
                }),
        }),
    link: (
        source: DataGridValueSource<TData, TextValue>,
        options: DataGridLinkColumnOptions<TData>,
    ) =>
        dataColumn<TData>({
            kind: "link",
            ...toAccessor(source),
            options,
            sortFn: sortText,
            cell: (context, meta) =>
                createElement(LinkCell<TData>, {
                    context,
                    meta,
                    href: options.href,
                    onActivate: options.onActivate,
                }),
        }),
    number: (
        source: DataGridValueSource<TData, NumberValue>,
        options: DataGridNumberColumnOptions<TData>,
    ) =>
        dataColumn<TData>({
            kind: "number",
            ...toAccessor(source),
            options,
            tone: options.tone,
            sortFn: sortNumber,
            cell: cellOf<TData>(NumberCell),
        }),
    money: (
        source: DataGridValueSource<TData, NumberValue>,
        options: DataGridMoneyColumnOptions<TData>,
    ) =>
        dataColumn<TData>({
            kind: "money",
            ...toAccessor(source),
            options,
            showPositive: options.showPositive,
            sortFn: sortNumber,
            cell: cellOf<TData>(MoneyCell),
        }),
    entity: (
        source: DataGridValueSource<TData, Nullable<DataGridEntityValue>>,
        options: DataGridEntityColumnOptions<TData>,
    ) => {
        const { display, skeleton } = toAvatarDisplay(options.display);
        return dataColumn<TData>({
            kind: "entity",
            ...toAccessor(source),
            options,
            sortFn: sortText,
            skeleton,
            cell: (context, meta) =>
                createElement(EntityCell<TData>, { context, meta, display }),
        });
    },
    account: (
        source: DataGridValueSource<TData, Nullable<DataGridAccountValue>>,
        options: DataGridAccountColumnOptions<TData>,
    ) =>
        dataColumn<TData>({
            kind: "account",
            ...toAccessor(source),
            options,
            sortFn: sortText,
            skeleton: options.showCode ? "text" : "institutionText",
            cell: (context, meta) =>
                createElement(AccountCell<TData>, {
                    context,
                    meta,
                    showCode: options.showCode ?? false,
                }),
        }),
    counterparty: (
        source: DataGridValueSource<TData, Nullable<DataGridCounterpartyValue>>,
        options: DataGridColumnOptions<TData>,
    ) =>
        dataColumn<TData>({
            kind: "counterparty",
            ...toAccessor(source),
            options,
            sortFn: sortText,
            cell: cellOf<TData>(CounterpartyCell),
        }),
    /**
     * Values are `dateOnly` (`YYYY-MM-DD`) strings, never timestamps. The key
     * type can't tell them apart, and any other string renders empty.
     */
    date: (
        source: DataGridValueSource<TData, TextValue>,
        options: DataGridDateColumnOptions<TData>,
    ) =>
        dataColumn<TData>({
            kind: "date",
            ...toAccessor(source),
            options,
            showYear: options.showYear,
            sortFn: sortDateOnly,
            skeleton: options.showYear ? "dateWithYear" : "date",
            cell: cellOf<TData>(DateCell),
        }),
    status: (
        source: DataGridValueSource<TData, TextValue>,
        options: DataGridStatusColumnOptions<TData>,
    ) =>
        dataColumn<TData>({
            kind: "status",
            ...toAccessor(source),
            options,
            sortFn: createStatusSort(options.order),
            skeleton: toStatusSkeleton(options.statuses),
            cell: (context, meta) =>
                createElement(StatusCell<TData>, {
                    context,
                    meta,
                    statuses: options.statuses,
                }),
        }),
    custom: <TValue>(options: DataGridCustomColumnOptions<TData, TValue>) => {
        const { render, ...columnOptions } = options;
        const { value, sortFn } = columnOptions;
        return dataColumn<TData>({
            kind: "custom",
            id: options.id,
            accessorFn: (row) => value?.(row) ?? undefined,
            options: { enableSorting: Boolean(sortFn), ...columnOptions },
            sortFn: sortFn
                ? (rowA, rowB) => sortFn(rowA.original, rowB.original)
                : sortText,
            cell: cellOf<TData>(render),
        });
    },
});

export type DataGridColumnBuilders<TData extends RowData> = ReturnType<
    typeof createColumns<TData>
>;
